import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { Box, Card, EmptySearchResult, Layout, Page, SkeletonBodyText, SkeletonThumbnail } from '@shopify/polaris';
import { formatDateToUTC, handleSort } from '#client/utils/helpers';

// Children components
import PurchaseOrderSummary from './components/PurchaseOrderSummary';
import AdvancedShippingNoticesTable from './components/AdvancedShippingNoticesTable';
import ReceivingDocumentsTable from './components/ReceivingDocumentsTable';
import ExpectedItemsTable from './components/ExpectedItemsTable';
import PURCHASE_ORDER_STRINGS from '#client/pages/PurchaseOrders/constants/purchaseOrders.en.json';
import ActivityLog from '#client/components/ActivityLog';
import { getPurchaseOrderDetailQueryOptions } from '#client/utils/queryOptions';
import React from 'react';
import PurchaseOrderBreadcrumbs from '../PurchaseOrderBreadcrumbs';

/**
 * @description PurchaseOrderDetail component fetches and displays the details of a purchase order.
 * @returns
 */
const PurchaseOrderDetail = () => {
    const navigate = useNavigate();

    const { asnId, poId } = useParams();
    const [parentData, setParentData] = useState(null);
    const [shopifyDataLoading, setShopifyDataLoading] = useState(true);

    const [asnState, setAsnState] = useState({
        rows: [],
        sortColumnIndex: null,
        sortDirection: 'none',
    });

    const [receivingState, setReceivingState] = useState({
        rows: [],
        sortColumnIndex: null,
        sortDirection: 'none',
    });

    const [expectedState, setExpectedState] = useState({
        rows: [],
        sortColumnIndex: null,
        sortDirection: 'none',
    });

    const mapAsnRows = (advancedShippingNotices, purchaseOrderLines) => {
        const quantityOrdered = purchaseOrderLines?.reduce((total, line) => total + line.quantity, 0) || 0;

        return advancedShippingNotices.map((asn) => {
            const expectedDate = asn.shipments?.[0]?.expectedDate;
            const advancedShippingNoticeId = asn.shipments?.[0]?.advancedShippingNoticeId;

            return {
                asnId: asn.netsuiteId,
                auditRequired: asn.isAuditTrigger ? PURCHASE_ORDER_STRINGS.AUDIT_REQUIRED : null,
                status: asn.status,
                expectedDate: formatDateToUTC(expectedDate),
                expectedDateRaw: expectedDate,
                quantityOrdered,
                advancedShippingNoticeId,
            };
        });
    };

    // Separate function to create lookup maps
    const createLookupMaps = (purchaseOrder, productData) => {
        const poLineMap = {};
        const productMap = {};

        purchaseOrder.purchaseOrderLines.forEach((line) => {
            poLineMap[line.sku] = line.quantity;
        });

        if (Array.isArray(productData)) {
            productData.forEach((product) => {
                if (product.sku) {
                    productMap[product.sku] = product;
                }
            });
        }

        return { poLineMap, productMap };
    };

    const calculateReceivingAggregations = (flattenedLines, poLineMap, documentCount) => {
        const aggregations = [];

        // First: filter and process relevant lines
        const relevantLineData = [];
        const aggregationMap = new Map();

        for (const item of flattenedLines) {
            // Handle both possible field names for quantity received
            const quantityReceived = item.line.quantityReceived;
            const expectedQty = poLineMap[item.sku];

            if (expectedQty !== undefined) {
                relevantLineData.push({
                    docIndex: item.docIndex,
                    quantityReceived,
                    expectedQty,
                    sku: item.sku,
                });
            }
        }

        // Second: create aggregation map by document index
        for (const item of relevantLineData) {
            if (!aggregationMap.has(item.docIndex)) {
                aggregationMap.set(item.docIndex, {
                    quantityReceived: 0,
                    quantityExpected: 0,
                    totalCartonLines: 0,
                });
            }

            const agg = aggregationMap.get(item.docIndex);

            agg.quantityReceived += item.quantityReceived;
            agg.quantityExpected += item.expectedQty;
            agg.totalCartonLines += item.expectedQty;
        }

        // Third: build final aggregations array in correct order
        for (let docIndex = 0; docIndex < documentCount; docIndex++) {
            const aggregation = aggregationMap.get(docIndex) || {
                quantityReceived: 0,
                quantityExpected: 0,
                totalCartonLines: 0,
            };

            aggregations.push(aggregation);
        }

        return aggregations;
    };

    const calculateExpectedItemsAggregations = (purchaseOrderLines, allReceivingLines) => {
        const aggregations = [];
        const skuQuantityMap = new Map();

        for (const docLine of allReceivingLines) {
            if (docLine.sku?.trim()) {
                const sku = docLine.sku.trim();
                const quantity = docLine.quantityReceived;

                skuQuantityMap.set(sku, (skuQuantityMap.get(sku) || 0) + quantity);
            }
        }

        // Second: lookup quantities for each purchase order line
        for (const line of purchaseOrderLines) {
            const sku = line.sku?.trim();
            const quantityReceived = skuQuantityMap.get(sku) || 0;

            aggregations.push({ quantityReceived });
        }

        return aggregations;
    };

    const mapReceivingDocuments = (receivingDocuments, aggregations, poLineMap, mappedAsns) => {
        return receivingDocuments
            .map((doc, index) => ({
                ...aggregations[index],
                ...doc,
                advancedShippingNoticeId: doc.advancedShippingNoticeId,
                asnNetsuiteId: mappedAsns?.find((asn) => asn.advancedShippingNoticeId === doc.advancedShippingNoticeId)?.asnId,
                isAuditRequired: doc.isAuditTrigger,
                createdAt: formatDateToUTC(doc.createdAt),
                createdAtRaw: doc.createdAt,
                lines: doc.lines.map((line) => ({
                    ...line,
                    quantityExpected: poLineMap[line.sku],
                    quantityReceived: line.quantityReceived,
                })),
                status: doc.status,
                updatedAt: formatDateToUTC(doc.updatedAt),
            }))
            .sort((a, b) => {
                const dateA = new Date(a.createdAtRaw);
                const dateB = new Date(b.createdAtRaw);
                return dateB - dateA; // Newest first (descending order)
            });
    };

    const mapExpectedItems = (purchaseOrderLines, aggregations, productMap, purchaseOrder) => {
        return purchaseOrderLines
            .map((line, index) => {
                const product = productMap ? productMap[line.sku] : null;
                return {
                    expectedShipDate: formatDateToUTC(purchaseOrder.expectedShipDate),
                    imageAlt: product?.variantImageAlt,
                    isLoadingProductData: !productMap,
                    netSuiteHumanPoNumber: purchaseOrder.netSuiteHumanPoNumber,
                    netSuiteHumanLineNumber: line.netSuiteHumanLineNumber,
                    product: product?.variantTitle,
                    sku: line.sku,
                    quantityExpected: line.quantity,
                    quantityReceived: aggregations[index].quantityReceived,
                    variantImage: product?.variantImage,
                    stockDescription: line.stockDescription,
                    itemNumberSourcing: line.itemNumberSourcing,
                    amazonSku: product?.amazonSku || null,
                    barcode: product?.barcode || null,
                };
            })
            .sort((a, b) => {
                if (parseInt(a.netSuiteHumanLineNumber) < parseInt(b.netSuiteHumanLineNumber)) return -1;
                if (parseInt(a.netSuiteHumanLineNumber) > parseInt(b.netSuiteHumanLineNumber)) return 1;
                return 0;
            });
    };

    // Query for purchase order details
    const fetchPurchaseOrderDetail = async (poId) => {
        const API_URL = `${import.meta.env.VITE_API_URL}/api/app/purchase-orders/${poId}`;
        const { data } = await axios.get(API_URL);
        return data.data;
    };

    const { data, isLoading } = useQuery({
        ...getPurchaseOrderDetailQueryOptions(poId),
        queryFn: () => fetchPurchaseOrderDetail(poId),
    });

    useEffect(() => {
        if (!data) return;

        const { purchaseOrder, productData } = data;

        if (purchaseOrder) {
            // Process data WITHOUT product info (immediate render)
            const processInitialData = () => {
                // Build a map of SKU to quantity from the PO lines
                const poLineMap = {};

                purchaseOrder.purchaseOrderLines.forEach((line) => {
                    poLineMap[line.sku] = line.quantity;
                });

                // Map ASN rows
                let mappedAsns = mapAsnRows(purchaseOrder.advancedShippingNotices, purchaseOrder.purchaseOrderLines);

                // Sort by expectedDateRaw (newest to oldest)
                mappedAsns = mappedAsns.sort((a, b) => {
                    let dateA = a.expectedDateRaw;
                    let dateB = b.expectedDateRaw;

                    if (!dateA || !dateB) return null;

                    // Convert to Date objects to be used in the return
                    dateA = new Date(a.expectedDateRaw);
                    dateB = new Date(b.expectedDateRaw);

                    return dateA - dateB; // Return the newest date first
                });

                const matchedAsnRow = mappedAsns.find((row) => String(row.asnId) === String(asnId));

                // Set parent data
                setParentData({
                    asnNetsuiteId: matchedAsnRow?.asnId,
                    cancelDate: formatDateToUTC(purchaseOrder.cancellationDate),
                    closeDate: formatDateToUTC(purchaseOrder.closeDate),
                    destinationLocation: purchaseOrder.locationId,
                    expectedShipDate: formatDateToUTC(purchaseOrder.expectedShipDate),
                    id: purchaseOrder.id,
                    isAuditRequired: matchedAsnRow?.isAuditTrigger,
                    netSuiteHumanPoNumber: purchaseOrder.netSuiteHumanPoNumber,
                    netsuiteId: purchaseOrder.netsuiteId,
                    purchaseOrder: purchaseOrder,
                    startDate: formatDateToUTC(purchaseOrder.startDate),
                    status: purchaseOrder.status,
                    vendorName: purchaseOrder.vendor,
                    productData,
                    navigateFromASN: false,
                });

                const documentInfo = [];
                for (let docIndex = 0; docIndex < purchaseOrder.receivingDocuments.length; docIndex++) {
                    const doc = purchaseOrder.receivingDocuments[docIndex];
                    documentInfo.push({ doc, docIndex });
                }

                const allLinesFlat = [];
                for (const { doc, docIndex } of documentInfo) {
                    for (const line of doc.lines) {
                        allLinesFlat.push({ line, docIndex });
                    }
                }

                const allLinesWithDocIndex = [];
                for (const { line, docIndex } of allLinesFlat) {
                    allLinesWithDocIndex.push({
                        docIndex,
                        line,
                        sku: line.sku,
                    });
                }

                const flattenedReceivingLines = allLinesWithDocIndex.map((item) => ({
                    docIndex: item.docIndex,
                    line: item.line,
                    sku: item.sku,
                }));

                const allReceivingLines = allLinesWithDocIndex.map((item) => item.line);

                // Calculate aggregations separately
                const receivingAggregations = calculateReceivingAggregations(flattenedReceivingLines, poLineMap, purchaseOrder.receivingDocuments.length);
                const expectedItemsAggregations = calculateExpectedItemsAggregations(purchaseOrder.purchaseOrderLines, allReceivingLines);

                // Map final data structures
                const mappedReceivingDocuments = mapReceivingDocuments(purchaseOrder.receivingDocuments, receivingAggregations, poLineMap, mappedAsns);

                const mappedExpectedItems = mapExpectedItems(
                    purchaseOrder.purchaseOrderLines,
                    expectedItemsAggregations,
                    null, // No product map yet
                    purchaseOrder
                );

                // Set initial state (renders immediately with basic data)
                setExpectedState((prev) => ({ ...prev, rows: mappedExpectedItems }));
                setAsnState((prev) => ({ ...prev, rows: mappedAsns }));
                setReceivingState((prev) => ({ ...prev, rows: mappedReceivingDocuments }));

                if (Array.isArray(productData) && productData.length > 0) {
                    setShopifyDataLoading(true);

                    setTimeout(() => {
                        const { productMap } = createLookupMaps(purchaseOrder, productData);
                        // Re-map expected items WITH product info
                        const updatedExpectedItems = mapExpectedItems(purchaseOrder.purchaseOrderLines, expectedItemsAggregations, productMap, purchaseOrder);

                        // Update state with enriched data
                        setExpectedState((prev) => ({ ...prev, rows: updatedExpectedItems }));
                        setShopifyDataLoading(false);
                    }, 0);
                } else {
                    setShopifyDataLoading(false);
                }
            };

            processInitialData();
        }
    }, [data, asnId]);

    const SkeletonSection = ({ lines, type }) => (
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ flex: '0 0 auto', marginRight: '16px', display: type === 'text-with-image' ? 'block' : 'none' }}>
                <SkeletonThumbnail size="small" />
            </div>
            <div style={{ flex: '1 1 0' }}>
                <SkeletonBodyText lines={lines} />
            </div>
        </div>
    );

    return (
        <>
            <PurchaseOrderBreadcrumbs nsId={parentData?.netSuiteHumanPoNumber} poId={poId} isPoLevel={true} />

            <Page title={PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_DETAIL.TITLE} fullWidth>
                <Layout>
                    <>
                        <Layout.Section>
                            <Card sectioned>{isLoading ? <SkeletonSection lines={3} type="text-only" /> : <PurchaseOrderSummary poId={poId} parentData={parentData} navigate={navigate} />}</Card>
                        </Layout.Section>

                        <Layout.Section>
                            <Card sectioned>
                                {isLoading ? (
                                    <SkeletonSection lines={3} type="text-only" />
                                ) : asnState.rows?.length > 0 ? (
                                    <AdvancedShippingNoticesTable
                                        asnRows={asnState.rows}
                                        setAsnRows={(rows) => setAsnState((prev) => ({ ...prev, rows }))}
                                        navigate={navigate}
                                        asnSortColumnIndex={asnState.sortColumnIndex}
                                        asnSortDirection={asnState.sortDirection}
                                        setAsnSortColumnIndex={(index) => setAsnState((prev) => ({ ...prev, sortColumnIndex: index }))}
                                        setAsnSortDirection={(direction) => setAsnState((prev) => ({ ...prev, sortDirection: direction }))}
                                        handleSort={handleSort}
                                        parentData={parentData}
                                    />
                                ) : (
                                    <Box padding="1000">
                                        <EmptySearchResult
                                            title={PURCHASE_ORDER_STRINGS.EMPTY_STATE.ASN_EMPTY_STATE_TITLE}
                                            description={PURCHASE_ORDER_STRINGS.EMPTY_STATE.ASN_EMPTY_STATE_DESCRIPTION}
                                            withIllustration
                                        />
                                    </Box>
                                )}
                            </Card>
                        </Layout.Section>

                        <Layout.Section>
                            <Card sectioned>
                                {isLoading ? (
                                    <SkeletonSection lines={3} type="text-only" />
                                ) : receivingState.rows?.length > 0 ? (
                                    <ReceivingDocumentsTable
                                        receivingRows={receivingState.rows}
                                        navigate={navigate}
                                        receivingSortColumnIndex={receivingState.sortColumnIndex}
                                        receivingSortDirection={receivingState.sortDirection}
                                        setReceivingSortColumnIndex={(index) => setReceivingState((prev) => ({ ...prev, sortColumnIndex: index }))}
                                        setReceivingSortDirection={(direction) => setReceivingState((prev) => ({ ...prev, sortDirection: direction }))}
                                        handleSort={handleSort}
                                        setReceivingRows={(rows) => setReceivingState((prev) => ({ ...prev, rows }))}
                                        parentData={parentData}
                                    />
                                ) : (
                                    <Box padding="1000">
                                        <EmptySearchResult
                                            title={PURCHASE_ORDER_STRINGS.EMPTY_STATE.RECEIVING_DOCUMENT_EMPTY_STATE_TITLE}
                                            description={PURCHASE_ORDER_STRINGS.EMPTY_STATE.RECEIVING_DOCUMENT_EMPTY_STATE_DESCRIPTION}
                                            withIllustration
                                        />
                                    </Box>
                                )}
                            </Card>
                        </Layout.Section>
                        <Layout.Section>
                            <Card sectioned>
                                {isLoading || shopifyDataLoading ? (
                                    <SkeletonSection lines={3} type="text-with-image" />
                                ) : expectedState.rows?.length > 0 ? (
                                    <ExpectedItemsTable
                                        expectedRows={expectedState.rows}
                                        expectedSortColumnIndex={expectedState.sortColumnIndex}
                                        expectedSortDirection={expectedState.sortDirection}
                                        setExpectedSortColumnIndex={(index) => setExpectedState((prev) => ({ ...prev, sortColumnIndex: index }))}
                                        setExpectedSortDirection={(direction) => setExpectedState((prev) => ({ ...prev, sortDirection: direction }))}
                                        handleSort={handleSort}
                                        setExpectedRows={(rows) => setExpectedState((prev) => ({ ...prev, rows }))}
                                        isLoadingProductData={shopifyDataLoading}
                                        purchaseOrder={parentData}
                                    />
                                ) : (
                                    <Box padding="1000">
                                        <EmptySearchResult
                                            title={PURCHASE_ORDER_STRINGS.EMPTY_STATE.EXPECTED_ITEMS_EMPTY_STATE_TITLE}
                                            description={PURCHASE_ORDER_STRINGS.EMPTY_STATE.EXPECTED_ITEMS_EMPTY_STATE_DESCRIPTION}
                                            withIllustration
                                        />
                                    </Box>
                                )}
                            </Card>
                        </Layout.Section>

                        <Layout.Section>
                            <Card>
                                {isLoading ? <SkeletonSection lines={3} type="text-only" /> : <ActivityLog objectType="ReceivingDocument" parentObjectType="PurchaseOrder" parentObjectId={poId} />}
                            </Card>
                        </Layout.Section>
                    </>
                </Layout>
            </Page>
        </>
    );
};

export default PurchaseOrderDetail;
